/**
 * <WatchlistTab />
 *
 * Lets a signed-in user persist saved searches (part-number, keywords,
 * category, tags, price range) and opt into email alerts when a new
 * matching listing is created. Backs onto `/api/me/watchlist`.
 */
import * as React from "react";
import { BellRing, Plus, Trash2 } from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import type { CategoryNode } from "../../../lib/types";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";

interface WatchItem {
  id: string;
  name: string;
  part_number?: string | null;
  q?: string | null;
  category_id?: string | null;
  tags: string[];
  min_price?: number | null;
  max_price?: number | null;
  notify_email: boolean;
  // Phase 19 — fitment filter (Phase 15 dialect). Phase 22 wires it
  // into both the create form and the per-row display.
  fitment_filter?: string | null;
  last_alerted_at?: string | null;
  created_at: string;
}

export function WatchlistTab() {
  const [items, setItems] = React.useState<WatchItem[]>([]);
  const [cats, setCats] = React.useState<CategoryNode[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.get<WatchItem[]>("/me/watchlist"),
        api.get<CategoryNode[]>("/categories?tree=true"),
      ]);
      setItems(w.data);
      setCats(c.data);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const remove = async (id: string) => {
    setError(null);
    try {
      await api.delete(`/me/watchlist/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  const added = (item: WatchItem) => setItems((prev) => [item, ...prev]);

  // Phase 22 — per-row PATCH toggle for notify_email. Optimistic update
  // so the switch flips instantly; rollback to the old value on error.
  const toggleNotify = async (w: WatchItem) => {
    const next = !w.notify_email;
    setItems((prev) =>
      prev.map((x) => (x.id === w.id ? { ...x, notify_email: next } : x)),
    );
    try {
      await api.patch(`/me/watchlist/${w.id}`, { notify_email: next });
    } catch (err) {
      setError(extractApiError(err));
      setItems((prev) =>
        prev.map((x) => (x.id === w.id ? { ...x, notify_email: !next } : x)),
      );
    }
  };

  return (
    <Card elevated data-testid="watchlist-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <BellRing className="h-5 w-5" aria-hidden /> Watchlist
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Save a search and we'll email you the instant a matching listing
            goes up.
          </p>
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <p
            className="mb-3 text-sm font-semibold text-brand"
            data-testid="watchlist-error"
          >
            {error}
          </p>
        )}
        <CreateWatchForm cats={cats} onCreated={added} />

        {items.length === 0 ? (
          <p
            className="text-ink-muted text-center py-6"
            data-testid="watchlist-empty"
          >
            {loading ? "Loading…" : "No saved searches yet."}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-border mt-4">
            {items.map((w) => (
              <li
                key={w.id}
                className="py-3 flex items-start gap-3 flex-wrap"
                data-testid={`watch-row-${w.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink flex items-center gap-2 flex-wrap">
                    {w.name}
                  </div>
                  <div className="text-xs text-ink-muted mt-0.5 font-mono">
                    {[
                      w.part_number && `part=${w.part_number}`,
                      w.q && `q="${w.q}"`,
                      w.category_id &&
                        `cat=${cats.find((c) => c.id === w.category_id)?.name ?? "?"}`,
                      w.tags.length ? `tags=${w.tags.join(",")}` : null,
                      w.min_price != null && `min=$${w.min_price}`,
                      w.max_price != null && `max=$${w.max_price}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {/* Phase 22 — display-only fitment hint when present. */}
                  {w.fitment_filter && (
                    <div
                      className="text-xs text-ink-muted mt-0.5"
                      data-testid={`watch-fitment-${w.id}`}
                    >
                      Fitment: <span className="font-mono">{w.fitment_filter}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  {/* Phase 22 — per-row PATCH toggle for notify_email.
                      48px tap target via the wrapping label. */}
                  <label
                    className="inline-flex items-center gap-2 text-xs font-semibold text-ink h-12 px-2 cursor-pointer"
                    htmlFor={`watch-notify-${w.id}`}
                  >
                    <input
                      id={`watch-notify-${w.id}`}
                      type="checkbox"
                      checked={w.notify_email}
                      onChange={() => toggleNotify(w)}
                      className="h-5 w-5 accent-brand"
                      data-testid={`watch-notify-toggle-${w.id}`}
                    />
                    Email alerts
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    leadingIcon={<Trash2 className="h-4 w-4" />}
                    onClick={() => remove(w.id)}
                    data-testid={`watch-delete-${w.id}`}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function CreateWatchForm({
  cats,
  onCreated,
}: {
  cats: CategoryNode[];
  onCreated: (w: WatchItem) => void;
}) {
  const [form, setForm] = React.useState({
    name: "",
    part_number: "",
    q: "",
    category_id: "",
    tags_csv: "",
    min_price: "",
    max_price: "",
    // Phase 22 — fitment filter, mirrors Phase 15 marketplace ?fitment=.
    fitment_filter: "",
    notify_email: true,
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: any = {
        name: form.name.trim(),
        notify_email: form.notify_email,
      };
      if (form.part_number.trim()) body.part_number = form.part_number.trim();
      if (form.q.trim()) body.q = form.q.trim();
      if (form.category_id) body.category_id = form.category_id;
      const tags = form.tags_csv
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (tags.length) body.tags = tags;
      if (form.min_price) body.min_price = Number(form.min_price);
      if (form.max_price) body.max_price = Number(form.max_price);
      if (form.fitment_filter.trim())
        body.fitment_filter = form.fitment_filter.trim();
      const r = await api.post<WatchItem>("/me/watchlist", body);
      onCreated(r.data);
      setForm({
        name: "",
        part_number: "",
        q: "",
        category_id: "",
        tags_csv: "",
        min_price: "",
        max_price: "",
        fitment_filter: "",
        notify_email: true,
      });
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border-2 border-surface-border bg-surface-sunken p-4 flex flex-col gap-3"
      data-testid="watch-create-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Name this search"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Yamaha F250 fuel pump"
          data-testid="watch-name"
        />
        <Input
          label="Part number"
          value={form.part_number}
          onChange={(e) => setForm({ ...form, part_number: e.target.value })}
          data-testid="watch-part-number"
        />
        <Input
          label="Keyword"
          value={form.q}
          onChange={(e) => setForm({ ...form, q: e.target.value })}
          data-testid="watch-q"
        />
        <Select
          label="Category"
          value={form.category_id}
          onChange={(e) => setForm({ ...form, category_id: e.target.value })}
          placeholder="Any category"
          data-testid="watch-category"
        >
          {cats.map((cat) =>
            cat.children.length === 0 ? (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ) : (
              <optgroup key={cat.id} label={cat.name.toUpperCase()}>
                {cat.children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </optgroup>
            ),
          )}
        </Select>
        <Input
          label="Tags (comma-separated)"
          value={form.tags_csv}
          onChange={(e) => setForm({ ...form, tags_csv: e.target.value })}
          data-testid="watch-tags"
        />
        {/* Phase 22 — fitment filter (Phase 15 marketplace dialect). */}
        <Input
          label="Fitment (make/model)"
          value={form.fitment_filter}
          onChange={(e) =>
            setForm({ ...form, fitment_filter: e.target.value })
          }
          placeholder="Yamaha F250"
          data-testid="watch-fitment"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Min $"
            type="number"
            min="0"
            value={form.min_price}
            onChange={(e) => setForm({ ...form, min_price: e.target.value })}
            data-testid="watch-min"
          />
          <Input
            label="Max $"
            type="number"
            min="0"
            value={form.max_price}
            onChange={(e) => setForm({ ...form, max_price: e.target.value })}
            data-testid="watch-max"
          />
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
        <input
          type="checkbox"
          checked={form.notify_email}
          onChange={(e) => setForm({ ...form, notify_email: e.target.checked })}
          className="h-5 w-5 accent-brand"
          data-testid="watch-notify"
        />
        Email me when a match appears
      </label>

      {err && (
        <p
          className="text-sm font-semibold text-brand"
          data-testid="watch-create-error"
        >
          {err}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={busy}
          leadingIcon={<Plus className="h-4 w-4" />}
          data-testid="watch-create-submit"
        >
          Save search
        </Button>
      </div>
    </form>
  );
}
